import { CLIENT_MESSAGES, EMOTE_IDS, type EmoteId, type EmoteMessage } from '@veriverse/shared';
import type { Room } from 'colyseus.js';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { EMOTE_FRAME, EMOTE_SHEETS } from '../game/emotes.js';
import { isTyping } from '../game/typing.js';
import { emoteAt, segmentAt, setEmoteWheelOpen, stepSegment } from '../state/emote-wheel.js';
import { prefersReducedMotion, useSettingsStore } from '../state/settings.js';

const SIZE = 340;
const OUTER = SIZE / 2 - 4;
const INNER = OUTER * 0.52;
/** Angular gap between segments, in degrees. */
const GAP = 2.5;
const SEGMENT_DEG = 360 / EMOTE_IDS.length;
const ICON_SCALE = 1.5;

const point = (radius: number, deg: number): string => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return `${SIZE / 2 + radius * Math.cos(rad)},${SIZE / 2 + radius * Math.sin(rad)}`;
};

/** SVG path of the ring segment `index` (segment 0 is centred on the top). */
function segmentPath(index: number): string {
  const start = index * SEGMENT_DEG - SEGMENT_DEG / 2 + GAP / 2;
  const end = start + SEGMENT_DEG - GAP;
  return [
    `M ${point(OUTER, start)}`,
    `A ${OUTER} ${OUTER} 0 0 1 ${point(OUTER, end)}`,
    `L ${point(INNER, end)}`,
    `A ${INNER} ${INNER} 0 0 0 ${point(INNER, start)}`,
    'Z',
  ].join(' ');
}

const iconCenter = (index: number): { left: number; top: number } => {
  const rad = ((index * SEGMENT_DEG - 90) * Math.PI) / 180;
  const radius = (OUTER + INNER) / 2;
  return { left: SIZE / 2 + radius * Math.cos(rad), top: SIZE / 2 + radius * Math.sin(rad) };
};

/**
 * The emote wheel: hold the emote key (Z by default) to show it, point with the mouse or step with
 * the arrow keys, release to play the emote over your head. Nothing happens if nothing is picked.
 */
export function EmoteWheel({ room }: { room: Room }) {
  const { t } = useTranslation();
  const emoteKeyCode = useSettingsStore((s) => s.keys.emote.keyCode);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number | undefined>();
  const selectedRef = useRef<number | undefined>(undefined);
  const openRef = useRef(false);
  const centre = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

  useEffect(() => {
    const choose = (segment: number | undefined) => {
      selectedRef.current = segment;
      setSelected(segment);
    };
    const close = (send: boolean) => {
      if (!openRef.current) return;
      openRef.current = false;
      setEmoteWheelOpen(false);
      setOpen(false);
      const emote = emoteAt(selectedRef.current);
      choose(undefined);
      if (send && emote) {
        const message: EmoteMessage = { emote };
        room.send(CLIENT_MESSAGES.emote, message);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.keyCode === emoteKeyCode) {
        if (event.repeat || openRef.current || isTyping()) return;
        centre.current = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        openRef.current = true;
        setEmoteWheelOpen(true);
        setOpen(true);
        return;
      }
      if (!openRef.current) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close(false);
        return;
      }
      const delta =
        event.key === 'ArrowRight' || event.key === 'ArrowDown'
          ? 1
          : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
            ? -1
            : undefined;
      if (delta) {
        event.preventDefault();
        choose(stepSegment(selectedRef.current, delta));
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.keyCode === emoteKeyCode) close(true);
    };
    const onMouseMove = (event: MouseEvent) => {
      if (!openRef.current) return;
      choose(segmentAt(event.clientX - centre.current.x, event.clientY - centre.current.y, OUTER));
    };
    const onBlur = () => close(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('blur', onBlur);
      if (openRef.current) setEmoteWheelOpen(false);
      openRef.current = false;
    };
  }, [room, emoteKeyCode]);

  if (!open) return null;
  const current = emoteAt(selected);
  return (
    <div
      className={prefersReducedMotion() ? 'emote-wheel still' : 'emote-wheel'}
      role="presentation"
      style={{ width: SIZE, height: SIZE }}
    >
      <svg width={SIZE} height={SIZE} aria-hidden="true">
        {EMOTE_IDS.map((id, index) => (
          <path
            key={id}
            d={segmentPath(index)}
            className={index === selected ? 'emote-segment selected' : 'emote-segment'}
          />
        ))}
      </svg>
      {EMOTE_IDS.map((id, index) => (
        <EmoteIcon key={id} id={id} index={index} active={index === selected} />
      ))}
      <div className="emote-wheel-label" aria-live="polite">
        {current ? t(`emotes.${current}`) : ''}
      </div>
    </div>
  );
}

function EmoteIcon({ id, index, active }: { id: EmoteId; index: number; active: boolean }) {
  const sheet = EMOTE_SHEETS[id];
  const { left, top } = iconCenter(index);
  const width = EMOTE_FRAME.width * ICON_SCALE;
  const height = EMOTE_FRAME.height * ICON_SCALE;
  const style = {
    left,
    top,
    width,
    height,
    marginLeft: -width / 2,
    marginTop: -height * 0.6,
    backgroundImage: `url(${sheet.url})`,
    backgroundSize: `${width * sheet.frames}px ${height}px`,
    '--emote-frames': sheet.frames,
    '--emote-shift': `${-width * sheet.frames}px`,
    '--emote-duration': `${sheet.frames / sheet.fps}s`,
  } as CSSProperties;
  return (
    <span
      className={active ? 'emote-icon active' : 'emote-icon'}
      style={style}
      aria-hidden="true"
    />
  );
}
